import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@project/ui";
import { Button, formatShort, Icon } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";
import {
  acceptHouseInvitationById,
  fetchMyPendingHouseInvitations,
  homeExpenseQueryKeys,
} from "@apps/dashboard/lib/home-expense-api";
import { ApiRequestError } from "@apps/dashboard/lib/api-fetch";
import { dashboardPaths } from "@apps/dashboard/lib/routes";

function invitationRoleLabel(t: (k: string) => string, role: string): string {
  if (role === "admin") return t("dashboardUi.members.roleAdmin");
  if (role === "member") return t("dashboardUi.members.roleMember");
  return role;
}

export default function PendingHouseInvitesPage(): JSX.Element {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: homeExpenseQueryKeys.myPendingInvitations(locale),
    queryFn: () => fetchMyPendingHouseInvitations(locale),
  });

  const acceptMut = useMutation({
    mutationFn: (invitationId: string) =>
      acceptHouseInvitationById(locale, { invitationId }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: homeExpenseQueryKeys.houses(locale) });
      void queryClient.invalidateQueries({
        queryKey: homeExpenseQueryKeys.myPendingInvitations(locale),
      });
      void queryClient.invalidateQueries({
        queryKey: homeExpenseQueryKeys.notificationsInbox(locale),
      });
      void queryClient.invalidateQueries({
        queryKey: homeExpenseQueryKeys.notificationUnread(locale),
      });
      if (res.joined && res.houseId) {
        navigate(dashboardPaths.houseMembers(res.houseId), { replace: true });
      }
    },
  });

  const items = listQuery.data ?? [];
  const rowError =
    acceptMut.isError && acceptMut.variables != null
      ? acceptMut.error instanceof ApiRequestError
        ? acceptMut.error.message
        : t("dashboardUi.auth.errors.generic")
      : null;

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <DashboardBackLink to={dashboardPaths.home}>{t("dashboardUi.searchPage.backHome")}</DashboardBackLink>
      <h1 className="font-headline text-2xl font-bold tracking-tight text-primary">
        {t("dashboardUi.pendingInvitationsPage.title")}
      </h1>
      <p className="mt-2 font-body text-body-md text-on-surface-variant">
        {t("dashboardUi.pendingInvitationsPage.subtitle")}
      </p>

      <section className="mt-10">
        {listQuery.isPending ? (
          <LoadingState />
        ) : listQuery.isError ? (
          <div className="space-y-3">
            <p className="font-body text-sm text-on-surface-variant">
              {t("dashboardUi.pendingInvitationsPage.loadError")}
            </p>
            <ErrorState onRetry={() => void listQuery.refetch()} />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center rounded-2xl border border-dashed border-outline-variant/30 bg-surface-container-lowest/50 px-8 py-16 text-center">
            <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Icon name="Mail" className="size-8" strokeWidth={1.25} />
            </div>
            <h2 className="font-headline text-lg font-bold text-on-surface">
              {t("dashboardUi.pendingInvitationsPage.emptyTitle")}
            </h2>
            <p className="mt-2 max-w-md font-body text-sm text-on-surface-variant">
              {t("dashboardUi.pendingInvitationsPage.emptyHint")}
            </p>
          </div>
        ) : (
          <ul className="flex flex-col gap-4">
            {items.map((inv) => {
              const busy =
                acceptMut.isPending && acceptMut.variables === inv.id;
              return (
                <li
                  key={inv.id}
                  className="rounded-2xl border border-outline-variant/20 bg-surface-container-low p-5"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="font-headline text-lg font-bold text-on-surface">
                        {inv.houseName}
                      </p>
                      <p className="mt-1 font-body text-sm text-on-surface-variant">
                        {t("dashboardUi.pendingInvitationsPage.inviterLine", {
                          inviterName: inv.inviterName,
                        })}
                      </p>
                      <p className="mt-1 font-body text-sm text-on-surface-variant">
                        {invitationRoleLabel(t, inv.role)}
                      </p>
                      <p className="mt-2 font-body text-xs text-on-surface-variant/80">
                        {t("dashboardUi.pendingInvitationsPage.expiresLabel", {
                          date: formatShort(inv.expiresAt),
                        })}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-stretch gap-2 sm:min-w-40">
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        className="font-headline font-semibold"
                        disabled={busy}
                        onClick={() => acceptMut.mutate(inv.id)}
                      >
                        {busy
                          ? t("dashboardUi.pendingInvitationsPage.accepting")
                          : t("dashboardUi.pendingInvitationsPage.acceptCta")}
                      </Button>
                    </div>
                  </div>
                  {rowError != null && acceptMut.variables === inv.id ? (
                    <p className="mt-3 font-body text-sm text-error" role="alert">
                      {rowError}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
